import { useEffect, useState, useCallback, useRef } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import Cookies from 'universal-cookie';
import CryptoJS from 'crypto-js';
import { Form, Button, Container } from 'semantic-ui-react';
import { getCookieOptions } from '@plone/volto/helpers/Cookies/cookies';
import { useSelector } from 'react-redux';
import Api from '@plone/volto/helpers/Api/Api';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import PlayPauseButton from './PlayPauseButton';
import SeekBar from './SeekBar';
import SpeedControl from './SpeedControl';
import VolumeControl from './VolumeControl';
import CurrentTimeDisplay from './CurrentTimeDisplay';
import AudioDurationDisplay from './AudioDurationDisplay';
import PlayIcon from './Icons/Play_arrow.svg';
import { getVideoPlayerData } from 'volto-base-addon/components/manage/Blocks/VideoJS/VideoPlayerDataHook';
import LiveAwareVideoPlayer from 'volto-base-addon/components/manage/Blocks/VideoJS/LiveAwareVideoPlayer';
import {
  useLiveStream,
  fetchLiveWorkflowActive,
} from 'volto-base-addon/components/manage/Blocks/VideoJS/useLiveStream';
import {
  PathKeyContext,
  withPathKeyParam,
} from 'volto-base-addon/helpers/pathKey';
import { outputStateOf } from 'volto-base-addon/helpers/mediaProcessingLogic';
import { getVar } from '../../../helpers/utils';
import EmbedMessage from './EmbedMessage';
import request from 'superagent';
import config from '@plone/volto/registry';

// Tooltip on the title link, in the media page's own language. Unlike the
// not-found page we do know the language here: resolve-uid gives us the
// language root before the content is even fetched.
const PLAY_IN_MONIVIESTIN = {
  fi: 'Toista Moniviestimessä',
  en: 'Play in Moniviestin',
};

const messages = defineMessages({
  requiredPathKey: {
    id: 'requiredPathKey',
    defaultMessage: 'A pathkey is required to access this resource.',
  },
  pathKeyDisplay: {
    id: 'pathKeyDisplay',
    defaultMessage: 'Pathkey is:',
  },
  pathKeyLabel: {
    id: 'pathKeyLabel',
    defaultMessage: 'Enter your pathkey',
  },
  pathkeySubmitButton: {
    id: 'pathkeySubmitButton',
    defaultMessage: 'Submit',
  },
  pathKeyOwner: {
    id: 'pathKeyOwner',
    defaultMessage: 'Pathkey owner:',
  },
  pathKeyOwnerEmail: {
    id: 'pathKeyOwnerEmail',
    defaultMessage: 'You can request the correct pathkey by emailing:',
  },
  wrongPathKey: {
    id: 'wrongPathKey',
    defaultMessage: 'That pathkey is not correct.',
  },
});

const EmbedPlayer = ({ uid, type, redirect = true, initialPathKey = null }) => {
  const { token } = useSelector((state) => state.userSession);
  const [audioSrc, setAudioSrc] = useState(null);
  const [forbiddenError, setForbiddenError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pathKeyInput, setPathKeyInput] = useState('');
  // In state, not a cookie: browsers discard cookies written inside a
  // third-party iframe, which made the form loop forever.
  const [pathKey, setPathKey] = useState(initialPathKey);
  const [videoPlayerData, setVideoPlayerData] = useState(null);
  const [videoBodyProps, setVideoBodyProps] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');
  const [videoDescription, setVideoDescription] = useState('');
  const [videoImage, setVideoImage] = useState('');
  const [isMobilePlayerOpen, setIsMobilePlayerOpen] = useState(false);
  const [mediaPage, setMediaPage] = useState(null);
  // Whether the media page's live-stream workflow is currently "active".
  // Resolved via a direct API call (fetchLiveWorkflowActive below) rather
  // than the redux content flow the media page block uses, since the embed
  // is a standalone page that must carry its own auth on every request.
  const [isLiveWorkflowActive, setIsLiveWorkflowActive] = useState(false);
  const audioRef = useRef(null);

  const intl = useIntl();

  // Layer 2: Pure observation (reads mediaPage, writes nothing back) — same
  // hook the media page block uses, so both share start/end messaging,
  // active polling, autoplay-on-go-live and live-not-active error recovery.
  const live = useLiveStream(mediaPage, isLiveWorkflowActive);

  const handleOpenPlayer = () => {
    setIsMobilePlayerOpen(true);
  };

  // The same helper the player's own fetches use, bound to the key we hold.
  const withPathKey = useCallback(
    (url) => withPathKeyParam(url, pathKey),
    [pathKey],
  );

  const fetchMediaPageByUid = useCallback(
    async (uid) => {
      const api = new Api();
      let mediaPageUrl;

      try {
        const resolved = await request.get(`/resolve-uid/${uid}`);
        mediaPageUrl = resolved?.body?.url;
      } catch (error) {
        // 404 means the UID is genuinely not ours, which is the dead-embed
        // case. Anything else is a transport or backend failure and must not
        // be reported to the viewer as "not found".
        return { error: error?.status === 404 ? 'notfound' : 'error' };
      }

      if (!mediaPageUrl) {
        return { error: 'notfound' };
      }

      const internalApiPath = config.settings.internalApiPath;
      const mediaPagePath = internalApiPath
        ? mediaPageUrl.replace(internalApiPath, '')
        : new URL(mediaPageUrl).pathname;

      try {
        return await api.get(withPathKey(mediaPagePath), {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (error) {
        const status = error.response?.status;
        // Only a pathkey 403 offers the viewer a way in; any other 403
        // (ContentExpired, plain permission denied) is terminal, like a 401.
        if (
          status === 403 &&
          error.response?.body?.error === 'PathKeyRequired'
        ) {
          setForbiddenError(error);
          return { error: 'forbidden' };
        }
        if (status === 404) {
          return { error: 'notfound' };
        }
        // The page exists but is not the viewer's to see. Nothing they can do
        // from a third-party iframe, so it ends on the "unavailable" message.
        if (status === 401 || status === 403) {
          return { error: 'unavailable' };
        }
        console.error('Error fetching media page content:', error);
        return { error: 'error' };
      }
    },
    [token, withPathKey],
  );

  const fetchChildren = useCallback(
    async (path) => {
      const api = new Api();
      try {
        // Fetch the media page's children enriched with the metadata columns
        // (output_state + subtitle_* + file_size) in a single request. This
        // works for anonymous users on unlisted pages (backend allows depth=1
        // children of an unlisted parent). NOTE: the rest of this component
        // still does its own per-transcoding @workflow check below — that
        // cleanup is deferred to a follow-up session.
        const metadataFields = [
          'output_state',
          'subtitle_task',
          'subtitle_language',
          'subtitle_fillers',
          'file_size',
        ];
        const qs = metadataFields
          .map((f) => `metadata_fields=${encodeURIComponent(f)}`)
          .join('&');
        const response = await api.get(
          withPathKey(`${flattenToAppURL(path)}?${qs}`),
          { headers: { Authorization: `Bearer ${token}` } },
        );
        return response?.items || [];
      } catch (error) {
        console.error('Error fetching children:', error);
        return [];
      }
    },
    [token, withPathKey],
  );

  const fetchAndCreateBlobUrl = useCallback(async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching audio data:', error);
      return null;
    }
  }, []);

  const parseBlockData = (mediaPage) => {
    if (!mediaPage || !mediaPage.blocks) {
      console.warn('No blocks found in mediaPage');
      return null;
    }
    return (
      Object.values(mediaPage.blocks).find(
        (block) => block['@type'] === 'mediapagevideojs',
      ) || null
    );
  };

  useEffect(() => {
    let ignore = false;
    const updatePlayerData = async () => {
      const api = new Api();

      if (!uid) {
        setLoadError('notfound');
        return;
      }

      // A retry with a newly entered pathkey has to clear the previous verdict,
      // otherwise the form stays on screen over the content it just unlocked.
      setForbiddenError(null);
      setLoadError(null);

      const mediaPage = await fetchMediaPageByUid(uid);

      if (mediaPage?.error === 'forbidden') {
        return;
      }

      if (mediaPage?.error === 'error') {
        setLoadError('error');
        return;
      }

      // A 401 or non-pathkey 403: the content exists but the viewer cannot see
      // it. The legacy site cannot show it either, so unlike a not-found there
      // is nothing to hand over to.
      if (mediaPage?.error === 'unavailable') {
        setLoadError('unavailable');
        return;
      }

      if (
        mediaPage?.error === 'notfound' ||
        mediaPage?.['@type'] !== 'media_page'
      ) {
        // The old site redirects embeds it cannot resolve to here, and we
        // redirect back for UIDs that predate this site; redirect=false is what
        // stops the two from ping-ponging. Only a real not-found hands over --
        // a backend failure here would otherwise bounce viewers off-site.
        const legacyUrl = getVar('RAZZLE_LEGACY_URL');
        if (legacyUrl && redirect) {
          const searchParams = new URLSearchParams(window.location.search);
          // set, not append: Embed.jsx reads this with URLSearchParams.get
          // (first value wins), so appending would sit behind any inbound one.
          searchParams.set('redirect', 'false');
          window.location.href = `${legacyUrl}/embed?${searchParams.toString()}`;
          return;
        }
        setLoadError('notfound');
        return;
      }

      setMediaPage(mediaPage);

      setVideoTitle(mediaPage.title);
      setVideoDescription(mediaPage.description);
      setVideoImage(mediaPage.image);

      const path = new URL(mediaPage['@id']).pathname;
      const children = await fetchChildren(path);

      // Completed transcodings — read straight from the output_state metadata
      // carried on the enriched children (no per-item @workflow request).
      const completedTranscodings = children
        .filter(
          (item) =>
            item['@type'] === 'transcoding' &&
            outputStateOf(item) === 'completed',
        )
        .map((item) => item['@id'].split('/').filter(Boolean).pop());

      const hasVideo = completedTranscodings.some((id) => id !== 'audio');
      const hasAudio = completedTranscodings.includes('audio');

      let effectiveType = type;
      if (!effectiveType) {
        effectiveType = !hasVideo && hasAudio ? 'audio' : 'video';
      }

      if (effectiveType === 'audio') {
        const audioTranscoding = children.find(
          (child) => child['@id'].split('/').filter(Boolean).pop() === 'audio',
        );

        if (!audioTranscoding) {
          // ?type=audio against a page that was never given an audio
          // transcoding. The media page exists, but nothing here can be
          // played, so say so rather than leave a blank frame.
          setLoadError('notfound');
          return;
        }

        const url = withPathKey(
          flattenToAppURL(mediaPage['@id']) + '/@download-presigned-audio',
        );
        const resp = await api.post(url, {
          data: {},
          headers: { Authorization: `Bearer ${token}` },
        });

        const blobUrl = await fetchAndCreateBlobUrl(resp.presigned_url);
        setAudioSrc(blobUrl);
      } else {
        const blockData = parseBlockData(mediaPage);

        if (!blockData) {
          // The page is there but carries no player block, so it is broken
          // rather than missing -- the viewer can do nothing about it either
          // way, but a blank frame tells them nothing at all.
          console.error('No valid video block found!');
          setLoadError('error');
          return;
        }

        const mediaData = await getVideoPlayerData(
          children,
          mediaPage.s3_object_id,
          mediaPage.live_stream_url,
          mediaPage.UID,
          token,
          api,
          null,
          null,
          intl,
          pathKey,
        );

        // Nothing to play: no transcoding ever completed, or they are gone.
        // VideoJS renders an empty box for an empty source list, so without
        // this the viewer gets a blank frame.
        //
        // Live pages are excluded -- they legitimately have no source until
        // the stream starts, in which case the live-stream workflow state
        // (fetched below) drives the placeholder/polling UI instead.
        const isLivePage = !!(
          mediaPage.live_stream_url ||
          mediaPage.live_stream_start ||
          mediaPage.live_stream_end
        );

        // Only worth the extra request for pages that actually carry live
        // stream data; every other media page skips it entirely.
        const liveWorkflowActive = isLivePage
          ? await fetchLiveWorkflowActive(mediaPage['@id'], {
              api,
              token,
              pathKey,
            })
          : false;
        if (!ignore) {
          setIsLiveWorkflowActive(liveWorkflowActive);
        }

        if (!mediaData.sources?.length && !isLivePage) {
          setLoadError('notfound');
          return;
        }

        const embedOptions = {
          ...blockData.options,
          ...mediaData,
        };

        setVideoPlayerData({
          ...blockData,
          options: embedOptions,
        });

        // The embed is served from the same origin as the site, so the public
        // media page URL is just our own origin plus the content path.
        const contentLang = mediaPage.language?.token === 'en' ? 'en' : 'fi';

        setVideoBodyProps({
          parentUid: mediaPage.UID,
          parentType: mediaPage['@type'],
          parentItems: children,
          titleBar: {
            title: mediaPage.title,
            href: window.location.origin + flattenToAppURL(mediaPage['@id']),
            linkTitle: PLAY_IN_MONIVIESTIN[contentLang],
          },
        });
      }
    };

    // getVideoPlayerData rejects when the stream fetch fails; without this the
    // player renders a blank frame. `ignore` stops a superseded run (a retry
    // with a new pathkey) from clobbering a newer success.
    updatePlayerData().catch((error) => {
      if (ignore) return;
      console.error('Embed: failed to load player data', error);
      setLoadError('error');
    });

    return () => {
      ignore = true;
    };
  }, [
    token,
    fetchMediaPageByUid,
    fetchChildren,
    fetchAndCreateBlobUrl,
    withPathKey,
    pathKey,
    uid,
    type,
    redirect,
    intl,
  ]);

  const ExpandableText = ({ text, lines = 2, isTitle = false }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div
        className="ExpandableText"
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          WebkitLineClamp: expanded ? 'unset' : lines,
          cursor: 'pointer',
          marginBottom: '10px',
        }}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setExpanded(!expanded);
          }
        }}
        tabIndex={0}
        role="button"
      >
        {isTitle ? <h2>{text}</h2> : <p>{text}</p>}
      </div>
    );
  };

  // Live options (autoplay-on-go-live, disabled playback rates) are applied
  // at render time, not inside the fetch effect, so they always reflect the
  // latest `live` state rather than whatever it was when the data loaded.
  let modifiedPlayerData = null;
  if (videoPlayerData) {
    modifiedPlayerData = {
      ...videoPlayerData,
      options: { ...videoPlayerData.options },
    };
    live.applyLiveOptions(modifiedPlayerData.options);
  }

  if (loadError) {
    return (
      <div id="page-embed">
        <div id="player-container">
          <EmbedMessage variant={loadError} />
        </div>
      </div>
    );
  }

  if (forbiddenError) {
    // forbiddenError is only ever set for a pathkey 403; every other 403 ends
    // on the "unavailable" message via fetchMediaPageByUid.
    const errorData = forbiddenError?.response?.body;

    const handlePathKeyInputChange = (e) => setPathKeyInput(e.target.value);

    const handleSubmit = (e) => {
      e.preventDefault();
      const hashedPathKey = CryptoJS.MD5(pathKeyInput).toString();

      // Best effort: saves same-site viewers re-entering the key after a
      // reload, but discarded in a third-party iframe.
      try {
        new Cookies().set('path_key', hashedPathKey, getCookieOptions());
      } catch (error) {
        // Storage blocked here; the state below is what counts.
      }

      // No reload — that is what used to lose the key. The effect refetches.
      setPathKey(hashedPathKey);
    };

    return (
      // #page-embed so the form scrolls inside the iframe rather than
      // overflowing it unreachably (see _embed.scss).
      <div id="page-embed" className="EmbedPathKeyGate">
        <Container className="view-wrapper">
          <h1>
            <FormattedMessage id="Forbidden" defaultMessage="Forbidden" />
          </h1>
          {/* Holding a key and still being asked for one means it is wrong,
              whether typed here or baked into the embed URL. */}
          {pathKey && (
            <p className="pathkey-rejected" role="alert">
              {intl.formatMessage(messages.wrongPathKey)}
            </p>
          )}
          <p>{intl.formatMessage(messages.requiredPathKey)}</p>
          {errorData?.pathkey_owner && (
            <p>
              {intl.formatMessage(messages.pathKeyOwner)}{' '}
              {errorData.pathkey_owner}
            </p>
          )}
          {errorData?.pathkey_owner_email && (
            <p>
              {intl.formatMessage(messages.pathKeyOwnerEmail)}{' '}
              <a href={`mailto:${errorData.pathkey_owner_email}`}>
                {errorData.pathkey_owner_email}
              </a>
            </p>
          )}
          {errorData?.pathkey && (
            <p>
              {intl.formatMessage(messages.pathKeyDisplay)}{' '}
              <strong>{errorData.pathkey}</strong>
            </p>
          )}
          <Form onSubmit={handleSubmit}>
            <Form.Field>
              <label htmlFor="pathkey-input">
                {intl.formatMessage(messages.pathKeyLabel)}
              </label>
              <input
                id="pathkey-input"
                type="text"
                value={pathKeyInput}
                onChange={handlePathKeyInputChange}
                required
              />
            </Form.Field>
            <Button type="submit">
              {intl.formatMessage(messages.pathkeySubmitButton)}
            </Button>
          </Form>
        </Container>
      </div>
    );
  }

  return (
    // The player's own fetches hit children that inherit the pathkey by
    // acquisition, so the key has to reach them too.
    <PathKeyContext.Provider value={pathKey}>
      <div id="page-embed">
        <div id="player-container">
          {modifiedPlayerData && videoBodyProps && (
            <div className="block video align center">
              <LiveAwareVideoPlayer
                live={live}
                playerData={modifiedPlayerData}
                bodyProps={{
                  parentContent: mediaPage,
                  ...videoBodyProps,
                  editMode: false,
                }}
              />
            </div>
          )}

          {audioSrc && (
            <div className="EmbedAudioPlayer">
              <div
                className={`MobileView ${isMobilePlayerOpen ? 'hidden' : ''}`}
              >
                <div className="Image-field">
                  <img
                    src={videoImage?.download}
                    className="AudioplayerImage"
                    alt=""
                  />
                </div>
                <div className="TitleDescription-container MobileplayerClosed">
                  {videoTitle && (
                    <ExpandableText text={videoTitle} lines={1} isTitle />
                  )}
                  {videoDescription && (
                    <ExpandableText text={videoDescription} lines={2} />
                  )}
                  <AudioDurationDisplay audioRef={audioRef} />
                </div>
                <button className="OpenPlayerButton" onClick={handleOpenPlayer}>
                  <img className="Play-icon" src={PlayIcon} alt="Play/Pause" />
                </button>
              </div>

              <div
                className={`MobilePlayer ${isMobilePlayerOpen ? '' : 'hidden'}`}
              >
                <div className="AudioPlayer">
                  <div className="TitleDescription-container MobilePlayeropen">
                    <div className="Title-container">
                      {videoTitle && (
                        <ExpandableText text={videoTitle} lines={1} isTitle />
                      )}
                    </div>
                    <button
                      className="ClosePlayerButton"
                      onClick={() => setIsMobilePlayerOpen(false)}
                    >
                      ✖
                    </button>
                    <div className="Description-container">
                      {videoDescription && (
                        <ExpandableText text={videoDescription} lines={2} />
                      )}
                    </div>
                  </div>
                  <audio ref={audioRef} src={audioSrc}>
                    <track kind="captions" src="" />
                  </audio>

                  <div className="SeekBarWrapper">
                    <PlayPauseButton
                      className="MobilePlayButton"
                      audioRef={audioRef}
                    />
                    <SeekBar audioRef={audioRef} />
                  </div>

                  <div className="Controls-container">
                    <PlayPauseButton
                      className="DesktopPlayButton"
                      audioRef={audioRef}
                    />
                    <VolumeControl audioRef={audioRef} />
                    <CurrentTimeDisplay audioRef={audioRef} />{' '}
                    <SpeedControl audioRef={audioRef} />
                  </div>
                </div>
              </div>

              <div className="DesktopPlayer">
                <div className="Image-field">
                  <img
                    src={videoImage?.download}
                    className="AudioplayerImage"
                    alt=""
                  />
                </div>
                <div className="AudioPlayer">
                  <div className="TitleDescription-container">
                    {videoTitle && (
                      <ExpandableText text={videoTitle} lines={1} isTitle />
                    )}
                    <div className="Description-container">
                      {videoDescription && (
                        <ExpandableText text={videoDescription} lines={2} />
                      )}
                    </div>
                  </div>
                  <audio ref={audioRef} src={audioSrc}>
                    <track kind="captions" src="" />
                  </audio>

                  <div className="Player-controls">
                    <div className="SeekBarWrapper">
                      <PlayPauseButton
                        className="MobilePlayButton"
                        audioRef={audioRef}
                      />
                      <SeekBar audioRef={audioRef} />
                    </div>

                    <div className="Controls-container">
                      <PlayPauseButton
                        className="DesktopPlayButton"
                        audioRef={audioRef}
                      />
                      <VolumeControl audioRef={audioRef} />
                      <SpeedControl audioRef={audioRef} />
                      <CurrentTimeDisplay audioRef={audioRef} />{' '}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </PathKeyContext.Provider>
  );
};

export default EmbedPlayer;
